import { useEffect, useState, } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { Rocket, Gem, Briefcase, ChevronDown, UserRound } from "lucide-react";

export default function Home() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [openFaq, setOpenFaq] = useState(null);

  const plans = [
    {
      name: "Starter",
      icon: <Rocket className="text-[#66ccff] w-6 h-6" />,
      price: billingCycle === "monthly" ? "₹499/mo" : "₹4999/yr",
      features: ["1 Project", "Basic Templates", "Community Support"],
      template: "starter",
    },
    {
      name: "Pro",
      icon: <Gem className="text-[#66ccff] w-6 h-6" />,
      price: billingCycle === "monthly" ? "₹999/mo" : "₹9999/yr",
      features: ["Unlimited Projects", "Premium Templates", "Priority Support"],
      template: "pro",
      popular: true,
    },
    {
      name: "Enterprise",
      icon: <Briefcase className="text-[#66ccff] w-6 h-6" />,
      price: billingCycle === "monthly" ? "₹1999/mo" : "₹19999/yr",
      features: ["Team Access", "Custom Templates", "Dedicated Manager"],
      template: "enterprise",
    },
  ];

  const templates = [
    { title: "Student Developer", img: "👨‍💻" },
    { title: "Photographer", img: "📸" },
    { title: "Freelancer Resume", img: "🧑‍💼" },
    { title: "Startup Founder", img: "🚀" },
  ];

  const features = [
    "✅ Designed for students & job seekers",
    "✅ Hosted on fast, reliable platforms",
    "✅ Free support and updates included",
    "✅ SEO & analytics ready",
    "✅ Built using React & Tailwind",
    "✅ Affordable & scalable",
  ];

   const testimonials = [
    {
      name: "Priya Sharma",
      role: "Aspiring Developer",
      quote:
        "Project Port helped me launch my portfolio in just a few clicks. I landed two internships in a month!",
    },
    {
      name: "Ravi Kumar",
      role: "Freelance Designer",
      quote:
        "I used the Freelancer template and it looks amazing on mobile and desktop. Highly recommended!",
    },
    {
      name: "Aisha Rahman",
      role: "Student @ BITS",
      quote:
        "Super easy to use and affordable. The templates are clean and professional. Love it!",
    },
  ];

  const faqs = [
    {
      q: "Is it beginner-friendly?",
      a: "Yes! Project Port is designed for students, job seekers, and beginners. No coding needed.",
    },
    {
      q: "Can I use my own domain?",
      a: "Yes, you can connect your custom domain after publishing your portfolio.",
    },
    {
      q: "Are the templates mobile-friendly?",
      a: "Absolutely. All templates are fully responsive and mobile optimized.",
    },
    {
      q: "How do I get support?",
      a: "We offer email support and a community forum to help you anytime.",
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes, you can cancel or switch plans at any time from your dashboard.",
    },
    {
      q: "What tech stack is used?",
      a: "React.js, Tailwind CSS, and reliable hosting services like Vercel/Netlify.",
    },
  ];

  return (
    <div className="bg-white text-black dark:bg-[#0a0a0a] dark:text-white relative overflow-x-hidden">
      {/* Floating Background Lights */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-20 animate-pulse">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#0077cc] rounded-full blur-3xl opacity-30 animate-spin-slow"></div>
        <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-[#66ccff] rounded-full blur-2xl opacity-20 animate-spin-slower"></div>
      </div>

      {/* Hero Section */}
      <section
        className="relative z-10 min-h-[90vh] flex flex-col items-center justify-center px-6 py-20 text-center"
        data-aos="fade-down"
      >
        <div className="absolute inset-0 bg-white/80 dark:bg-black/60 backdrop-blur-md z-0" />
        <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 w-[600px] h-[600px] bg-[#0077cc] opacity-20 rounded-full blur-3xl animate-ping-slow z-0" />

        <img
          src="/logo.png"
          alt="Project Port Logo"
          className="h-32 w-32 md:h-40 md:w-40 mb-6 z-10 drop-shadow-2xl animate-bounce"
        />

        <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold mb-4 z-10 leading-tight">
          <span className="bg-gradient-to-r from-[#0077cc] via-[#00bfff] to-[#0077cc] bg-clip-text text-transparent animate-pulse">
            Don’t just build resumes.
          </span>
          <br />
          <span className="text-black dark:text-white">Build your brand.</span>
        </h1>

        <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 max-w-xl md:max-w-2xl mb-8 z-10">
          Your personal portfolio website — modern, student-friendly, and professional.
        </p>

        <Link to="/get-started" className="z-10">
          <button className="bg-[#0077cc] px-8 py-3 rounded-full text-white hover:bg-[#005fa3] shadow-xl transition-transform duration-300 hover:scale-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#66ccff]">
            Get Started
          </button>
        </Link>
      </section>

      {/* Pricing Plans */}
      <section className="relative z-10 px-6 py-20" data-aos="fade-up">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-[#0077cc] text-center">
          Pricing Plans
        </h2>
        <div className="flex justify-center mb-12 space-x-4 text-sm font-medium">
        <button
          onClick={() => setBillingCycle("monthly")}
          className={`px-4 py-2 rounded-full ${
            billingCycle === "monthly"
              ? "bg-[#0077cc] text-white"
              : "bg-gray-200 dark:bg-gray-800 text-black dark:text-white"
          } transition`}
        >
          Monthly
        </button>
        <button
          onClick={() => setBillingCycle("yearly")}
          className={`px-4 py-2 rounded-full ${
            billingCycle === "yearly"
              ? "bg-[#0077cc] text-white"
              : "bg-gray-200 dark:bg-gray-800 text-black dark:text-white"
          } transition`}
        >
          Yearly
        </button>
      </div>

      {/* Plans */}
      <div className="grid gap-10 md:grid-cols-3 max-w-6xl mx-auto">
        {plans.map((plan, index) => (
          <div
            key={index}
            className={`relative bg-gray-100 dark:bg-[#121212] border border-[#0077cc] rounded-2xl p-8 shadow-md hover:shadow-lg hover:scale-[1.03] transition-all duration-300 flex flex-col justify-between`}
          >
            {/* Most Popular Tag */}
            {plan.popular && (
              <span className="absolute -top-4 left-4 bg-gradient-to-r from-[#0077cc] to-[#66ccff] text-white text-xs font-semibold px-3 py-1 rounded-full shadow-md">
                MOST POPULAR
              </span>
            )}

            {/* Icon + Title */}
            <div className="mb-4 flex items-center gap-3">
              {plan.icon}
              <h3 className="text-2xl font-bold text-[#0077cc] dark:text-[#66ccff]">
                {plan.name}
              </h3>
            </div>

            <p className="text-3xl font-semibold text-black dark:text-white mb-6">
              {plan.price}
            </p>

            <ul className="space-y-3 text-sm text-gray-700 dark:text-gray-300 mb-8">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-center gap-2">
                  <span className="text-[#0077cc] dark:text-[#66ccff]">✔</span>
                  {feature}
                </li>
              ))}
            </ul>

            <Link
              to={`/get-started?template=${plan.template}`}
              className="text-center py-3 rounded-full font-medium bg-gradient-to-r from-[#0077cc] to-[#66ccff] text-white hover:from-[#005fa3] hover:to-[#66ccff] transition"
            >
              Choose Plan
            </Link>
          </div>
        ))}
      </div>
      </section>

      {/* Templates Preview */}
      <section className="relative z-10 px-6 py-20 text-center" data-aos="zoom-in">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-[#0077cc]">
          Templates Preview
        </h2>
        <div className="grid md:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {templates.map((tpl, i) => (
            <div
              key={i}
              className="bg-white dark:bg-[#1a1a1a] rounded-lg p-6 hover:bg-[#005fa3] hover:text-white transition-all duration-300 transform hover:scale-105 cursor-pointer"
              data-aos="flip-left"
              data-aos-delay={i * 100}
            >
              <div className="text-5xl mb-4">{tpl.img}</div>
              <h3 className="text-xl font-semibold">{tpl.title}</h3>
            </div>
          ))}
        </div>
        <Link to="/templates">
          <button className="mt-10 bg-[#0077cc] text-white px-6 py-2 rounded-full hover:bg-[#005fa3] transition-all duration-300 hover:scale-105">
            See All Templates
          </button>
        </Link>
      </section>

      {/* Features Section */}
      <section
        className="relative z-10 px-6 py-20 bg-gray-100 dark:bg-[#121212] text-center text-gray-800 dark:text-gray-300"
        data-aos="fade-up"
      >
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-[#0077cc]">
          Why Choose Project Port?
        </h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto text-left">
          {features.map((f, i) => (
            <div key={i} className="text-base md:text-lg" data-aos="fade-right" data-aos-delay={i * 100}>
              {f}
            </div>
          ))}
        </div>
      </section>
     <section className="relative z-10 px-6 py-20 bg-gray-100 dark:bg-[#121212] text-center" data-aos="fade-up">
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-[#0077cc]">What Our Users Say</h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white dark:bg-[#1a1a1a] p-6 rounded-xl shadow-md hover:shadow-xl transition">
              <UserRound className="mx-auto text-[#0077cc] dark:text-[#66ccff] mb-4 w-10 h-10" />
              <p className="text-lg italic text-gray-700 dark:text-gray-300 mb-4">“{t.quote}”</p>
              <h4 className="font-semibold text-[#0077cc] dark:text-[#66ccff]">{t.name}</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t.role}</p>
            </div>
          ))}
        </div>
      </section>

      {/* --- FAQs Section --- */}
      <section className="relative z-10 px-6 py-20 text-center bg-white dark:bg-[#0a0a0a]" data-aos="fade-up">
        <h2 className="text-3xl md:text-4xl font-bold mb-10 text-[#0077cc]">Frequently Asked Questions</h2>
        <div className="max-w-4xl mx-auto text-left space-y-6">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className="border-b border-gray-300 dark:border-gray-700 pb-4 cursor-pointer transition-all duration-300"
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-[#0077cc] dark:text-[#66ccff]">
                  {faq.q}
                </h3>
                <ChevronDown
                  className={`w-5 h-5 text-[#0077cc] dark:text-[#66ccff] transform transition-transform duration-300 ${
                    openFaq === i ? "rotate-180" : ""
                  }`}
                />
              </div>
              {openFaq === i && (
                <p className="mt-2 text-gray-700 dark:text-gray-300">{faq.a}</p>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 px-6 py-20 text-center" data-aos="fade-up">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#0077cc]">
          Ready to Launch Your Portfolio?
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg">
          Let Project Port help you stand out. It takes less than 5 minutes to get started.
        </p>
        <Link to="/get-started">
          <button className="bg-[#0077cc] px-8 py-3 text-white rounded-full hover:bg-[#005fa3] transition-transform duration-300 hover:scale-110 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#66ccff]">
            Launch Now
          </button>
        </Link>
      </section>
    </div>
  );
}
